import { inject, Injectable } from '@angular/core';
import { MockService } from '@mock/mock.service';
import { delay, Observable } from 'rxjs';
import { IApiResponse } from '@app/api/interfaces/common/api-response.interface';
import { convertStringInToSumOfAllCharacters } from '@app/shared/utils/string';
import { ChartDataSoundStatus } from '@app/core/enums/chart-data-sound-status.enum';
import {
  ILeakScoreDateChartData,
  ILeakScoreDateChartDataQueryParams,
  ILeakScoreDateChartDataValue,
} from '@app/api/interfaces/chart-data/date-leak-score';
import { LoggerDeploymentsDataMockService } from '@mock/data-services/logger-deployments-data-mock.service';
import { ILoggerDeploymentData } from '@app/api/interfaces/logger-deployment-data.interface';
import { generateDateDateChartData, sortByDate } from '@mock/utils/date-chart-data';
import { ILeakScoreDateChartDataRange } from '@app/api/interfaces/chart-data/date-leak-score';

@Injectable({ providedIn: 'root' })
export class LoggerDeploymentDateLeakScoreMockService extends MockService {
  private loggerDeploymentsDataMockService = inject(LoggerDeploymentsDataMockService);

  public get(
    projectId: string,
    deploymentId: string,
    filter: ILeakScoreDateChartDataQueryParams
  ): Observable<IApiResponse<ILeakScoreDateChartData, object>> {
    const deployment = this.loggerDeploymentsDataMockService.loggerDeploymentsData.find(
      el => el.deploymentId === deploymentId
    );

    const dataAll: ILeakScoreDateChartData = deployment
      ? this.generateAccordingToDeployment(deployment)
      : { ranges: [] };

    const data = sortByDate<ILeakScoreDateChartDataValue, ILeakScoreDateChartDataRange>(dataAll, filter);

    return this.mockResponse<IApiResponse<ILeakScoreDateChartData, object>>(
      'LOGGER_DEPLOYMENT_DATE-LEAK_SCORE:get',
      {
        data,
        meta: {},
      },
      arguments
    ).pipe(delay(this.requestDelay));
  }

  private generateAccordingToDeployment(deployment: ILoggerDeploymentData): ILeakScoreDateChartData {
    return generateDateDateChartData<ILeakScoreDateChartDataValue, ILeakScoreDateChartDataRange>(
      new Date().toISOString() /* deployment.deployTime */,
      (_, index) => ({
        deploymentId: deployment.deploymentId,
        serialNo: deployment.serialNo,
        warningThreshold: [43, 58][index % 1],
        criticalThreshold: [64, 71][index % 1],
        // this will be replaced!!!!
        collected: '',
        deployed: '',
      }),
      (date: string, index: number) => {
        let leakScore = convertStringInToSumOfAllCharacters(date + deployment.deploymentId) % 100;

        const isExistExtraBigData = convertStringInToSumOfAllCharacters(deployment.deploymentId) % 2 > 1;

        leakScore =
          leakScore < 20 && !isExistExtraBigData
            ? Math.round(leakScore / 2)
            : Math.round(leakScore * (leakScore % 4 || 1));
        leakScore = leakScore > 100 ? 100 : leakScore < 10 ? 0 : leakScore;

        const noSoundGroupSize = 5;
        const noSoundStartIndex = convertStringInToSumOfAllCharacters(deployment.deploymentId + 'ai') % 20;
        const status =
          index >= noSoundStartIndex && index < noSoundStartIndex + noSoundGroupSize
            ? ChartDataSoundStatus.NoSound
            : undefined;

        return {
          date,
          leakScore,
          status,
        };
      },
      [7, 8, 9, 10, 11, 12, 1, 2]
    );
  }
}
